import { FC, ReactNode, createContext, useEffect, useState } from 'react';

import { applyThemeColors } from '@/lib/utils';
import { type AppConfig } from '@/lib/types';
import { defaultConfig } from '@/lib/constants';

const ConfigContext = createContext<AppConfig | undefined>(undefined);

const ConfigProvider: FC<{ children: ReactNode; appConfig: AppConfig }> = ({
	children,
	appConfig,
}) => {
	const [config, setConfig] = useState<AppConfig | undefined>(
		(appConfig satisfies AppConfig) ?? defaultConfig
	);

	useEffect(() => {
		if (appConfig satisfies AppConfig) {
			setConfig(appConfig);
		} else {
			console.error(
				`${JSON.stringify(appConfig)} does not satisfy AppConfig structure. Loaded Chatbot with default values.`
			);
			setConfig(defaultConfig);
		}
	}, [appConfig]);

	useEffect(() => {
		const elementId = 'chat-window-body';

		if (config?.theme?.primary) {
			const { color, textColor } = config.theme.primary;
			applyThemeColors(elementId, { color, textColor }, true);
		}

		if (config?.theme?.secondary) {
			const { color, textColor } = config.theme.secondary;
			applyThemeColors(elementId, { color, textColor }, false);
		}

		if (config?.theme?.font) {
			if (typeof document !== 'undefined') {
				document
					.getElementById(elementId)
					?.style.setProperty('font-family', config?.theme?.font);
			}
		}
	}, [config]);

	return (
		<ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
	);
};

export { ConfigContext, ConfigProvider };
